<template>
  <BGModal :shown="dialogShown"
           :title="`${$t('admin_series.detail_title')} ${series ? '(' + $sanitizeHtml(series.name) + ')' : ''}`">
    <template #content>
      <v-container class="edit-series-content" fluid v-if="series">
        <v-form v-model="isFormValid"
                ref="seriesForm">
          <v-row class="flex-grow-1">
            <v-col cols="8" class="d-flex session-selection">
              <v-input :rules="selectedSessionIdRules"
                       v-model="selectedSessionIds"
                       hide-details="auto">
                <v-container fluid class="flex-grow-1 d-flex flex-column pa-0">
                  <v-row class="flex-grow-0">
                    <v-col>
                      <legend class="v-label theme--light">{{ $t('admin_series.sessions') }}</legend>
                    </v-col>
                  </v-row>
                  <v-row class="flex-grow-1" no-gutters>
                    <v-col>
                      <SessionList full-height
                                   hide-search
                                   :event-id="series.event.id"
                                   show-select
                                   :refresh-trigger.sync="sessionRefreshTrigger"
                                   v-model="selectedSessionIds"></SessionList>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
            <v-col cols="4">
              <v-text-field readonly
                            :label="$t('admin_hkiarace.event')"
                            :value="$sanitizeHtml(series.event.name)"></v-text-field>
              <v-text-field readonly
                            :label="$t('admin_event.signup_mode')"
                            :value="signupModeLabel"></v-text-field>
              <v-text-field outlined
                            :label="$t('admin_session.name_en')"
                            v-model="series.name_en"
                            :rules="requiredRules"></v-text-field>
              <v-text-field outlined
                            :label="$t('admin_session.name_zh')"
                            v-model="series.name_zh"
                            :rules="requiredRules"></v-text-field>
              <v-text-field outlined
                            :label="$t('admin_event.order_no')"
                            v-model="series.order_no"
                            type="number"
                            min="0"
                            step="1"
                            :rules="requiredRules"></v-text-field>
              <v-input :rules="[
                      value => (unlimitedQuota || !!value) || $t('register.required_fields'),
                    ]"
                       v-model="series.quota"
                       v-if="series.event.signup_mode === 2">
                <v-container fluid class="px-0">
                  <v-row>
                    <v-col class="pb-0">
                      <v-text-field outlined
                                    :label="$t('admin_series.quota')"
                                    type="number"
                                    v-model="series.quota"
                                    hide-details
                                    :disabled="unlimitedQuota">
                      </v-text-field>
                    </v-col>
                    <v-col cols="2" class="pb-0">
                      <v-checkbox :label="$t('unlimited')"
                                  v-model="unlimitedQuota"></v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </template>

    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               @click="$refs.seriesForm.validate() && save()">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import BGModal from "@/components/BGModal";
import {computed, ref, watch} from "@vue/composition-api";
import SessionList from "@/components/event/session/SessionList";

export default {
  name: 'EditSeriesModal',
  components: {SessionList, BGModal},
  props: {
    shown: Boolean,
    seriesId: String,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);

    const tabIndex = ref(0);

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          tabIndex.value = 0;
          sessionRefreshTrigger.value = true;
          getSeries();
        }
      }

      tabIndex.value = 0;
    });
    const cancel = function () {
      emit('cancel');
    };

    const isFormValid = ref(false);
    const series = ref(null);

    const sessionRefreshTrigger = ref(false);
    const selectedSessionIdRules = ref([
      value => value.length > 0 || root.$t('admin_series.no_session_msg'),
    ]);
    const selectedSessionIds = ref([]);
    const getSeries = function () {
      root.$callAPI('event/series/', {id: props.seriesId}, (response) => {
        series.value = response.body.series;
        selectedSessionIds.value = response.body.series.sessions.map((s) => s.id);
        unlimitedQuota.value = series.quota === -1;
      })
    };
    const signupModeLabel = computed(() => {
      if (series.value) {
        return {
          "-1": root.$t('event_signup_mode.disabled'),
          "1": root.$t('event_signup_mode.single_series'),
          "2": root.$t('event_signup_mode.multi_series'),
          "3": root.$t('event_signup_mode.multi_series'),
        }[series.value.event.signup_mode];
      }
    })
    const unlimitedQuota = ref(false);

    const save = function () {
      let params = {
        ...series.value,
        series_id: props.seriesId,
        quota: (series.value.event.signup_mode === 2 && !unlimitedQuota.value) ? series.value.quota : -1,
        session_ids: selectedSessionIds.value,
      }

      root.$callAPI('event/series/edit/', params, (response) => {
        emit('finish', response.body.series);
      });
    }

    return {
      dialogShown,
      tabIndex,
      cancel,

      isFormValid,
      requiredRules,

      signupModeLabel,

      series,
      selectedSessionIdRules,
      sessionRefreshTrigger,
      selectedSessionIds,
      unlimitedQuota,

      save,
    }
  },
}
</script>

<style lang="less">
.edit-series-content {
  display: flex;
  height: 100%;
  flex-direction: column;

  .v-form {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;

    .session-selection {
      .v-input {
        //flex: 1 1 auto;
        display: flex;
        flex-direction: column;

        .v-input__control {
          display: flex;
          flex-direction: column;

          .v-input__slot {
            flex: 1 1 auto;
            flex-direction: column;
          }

          .v-messages {
            flex: 0 0 auto;
          }
        }
      }
    }
  }
}
</style>
